import type { User } from '@/types';

/**
 * What to call the user in the app chrome (Phase 15, PULL-05).
 *
 * Exported from one place and imported by both shells, because two shells that
 * each decide this for themselves are two shells that will eventually disagree
 * about who is signed in.
 *
 * ── Why there is a fallback at all ─────────────────────────────────────────
 *
 * Signup is magic-link by email, so every account that existed before this
 * shipped has `display_name === null`, and most accounts will keep it that way
 * for a while. An identity line rendering nothing reads as a broken session,
 * not as a considered blank.
 *
 * The fallback is the WHOLE email address (operator direction, 2026-08-19).
 *
 * This reverses the local-part fallback PULL-05 shipped with. That version
 * traded away identification for discretion on a shared screen, and the trade
 * does not hold up: local parts are routinely not names. `info@`, `kontakt@`,
 * `m.horndt@`, `marcus+uat18@` all render as something that is either nobody or
 * the wrong person, and an identity line naming the wrong person is worse on a
 * shared screen than one naming the right address. The address is the only
 * thing we actually know about her until she types a name.
 *
 * Discretion is preserved where it can be earned rather than guessed: she can
 * set a display name, and the moment she does, the address stops rendering.
 *
 * ── The fallback is never persisted ────────────────────────────────────────
 *
 * This is a RENDERING decision and it lives here, in the SPA, on purpose. The
 * column stays null until she types something, so her Art. 20 export never
 * claims she chose a name she did not choose. `ExportDisplayNameTest` asserts
 * exactly that, and it is the reason the server does not compute this.
 */
export function resolveDisplayName(user: Pick<User, 'display_name' | 'email'> | null): string {
  const chosen = user?.display_name?.trim();
  if (chosen) return chosen;

  const email = user?.email?.trim();
  if (email) return email;

  return '';
}

/** True when the shell is showing a fallback rather than a name she chose. */
export function isFallbackName(user: Pick<User, 'display_name'> | null): boolean {
  return !user?.display_name?.trim();
}
